import {RowSubmissionRange} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {PotentialCellErrors} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {ValidationRuleError} from 'notebook/tables/excess/Util';

/**
 * Given a 2D array of EvaluatedCellError's, return a list of unique error messages.
 */
export function getUniqueErrorMessages(errorRows: Array<Array<EvaluatedCellError>>): Array<string> {
  const uniqueErrors = new Set<string>();
  errorRows.forEach((rowErrors: Array<EvaluatedCellError>) => {
    rowErrors.forEach((cellError: EvaluatedCellError) => {
      const messages = Array.isArray(cellError) ? cellError.map((e) => e.message) : [cellError.message];
      messages.forEach((message: string) => {
        uniqueErrors.add(message);
      });
    });
  });

  return [...uniqueErrors];
}

/**
 * Given a table, return a 2D array of EvaluatedCellError's.
 */
export function getEvaluatedRowErrors(
  table: Table | undefined,
  selection?: RowSubmissionRange
): Array<Array<EvaluatedCellError>> {
  if (!table) {
    return [];
  }

  const rowErrors: Array<Array<EvaluatedCellError>> = [];

  table.table.forEach((row: Array<Cell>, index: number) => {
    // start is inclusive, end is exclusive
    if (selection ? index < selection.start || selection.end <= index : false) {
      return; // don't include errors outside the selection range for row-based submissions
    }

    const cellErrors = row
      .map((cell: Cell) => cell.error)
      .filter((error: PotentialCellErrors): error is EvaluatedCellError => Boolean(error));
    if (cellErrors.length) {
      rowErrors.push(cellErrors);
    }
  });
  return rowErrors;
}

export function hasOnlyOverridableErrors(tableErrors: Array<Array<EvaluatedCellError>>): boolean {
  const rowErrors = tableErrors.flat();
  if (!rowErrors.length) {
    return false;
  }

  return rowErrors.every((cellError: EvaluatedCellError) => {
    if (Array.isArray(cellError)) {
      return cellError.every((error: ValidationRuleError) => error?.overridable);
    } else {
      return (cellError as ValidationRuleError)?.overridable;
    }
  });
}
